import { useEffect, useRef } from 'react';
import { BLOCKMETA_CONFIRMED_SUBJECT, getBlockmetaStore } from '../blockchain/blockmeta/index.js';
import { sdkEvents } from '../core/events.js';
const INVALID_PAYLOAD_LOG_COOLDOWN_MS = 30_000;
export function useBlockmetaFeedSubscription(client, jwt, mode) {
  const canSubscribe = jwt.allow_sub.includes(BLOCKMETA_CONFIRMED_SUBJECT);
  const lastInvalidLogAtByReason = useRef(new Map());
  useEffect(() => {
    const store = getBlockmetaStore();
    if (client === null || mode === 'off') {
      store.clear();
      return;
    }
    if (!canSubscribe) {
      store.clear();
      sdkEvents.emit('blockmeta:status', {
        level: 'warn',
        message: 'blockchain.blockmeta.subscription_disabled',
        mode,
        subject: BLOCKMETA_CONFIRMED_SUBJECT,
        reason: 'missing_allow_sub'
      });
      return;
    }
    let cancelled = false;
    let subscription;
    try {
      subscription = client.subscribe(BLOCKMETA_CONFIRMED_SUBJECT);
    } catch (err) {
      sdkEvents.emit('blockmeta:status', {
        level: 'warn',
        message: 'blockchain.blockmeta.subscription_failed',
        subject: BLOCKMETA_CONFIRMED_SUBJECT,
        err: err instanceof Error ? err.message : String(err)
      });
      return;
    }
    sdkEvents.emit('blockmeta:status', {
      level: 'info',
      message: 'blockchain.blockmeta.subscription_ready',
      mode,
      subject: BLOCKMETA_CONFIRMED_SUBJECT
    });
    void (async () => {
      try {
        for await (const msg of subscription) {
          const nowMs = Date.now();
          const result = store.acceptPayload(msg.subject, msg.data, nowMs);
          if (!result.ok) {
            const lastLogAt = lastInvalidLogAtByReason.current.get(result.reason) ?? 0;
            if (nowMs - lastLogAt < INVALID_PAYLOAD_LOG_COOLDOWN_MS) continue;
            lastInvalidLogAtByReason.current.set(result.reason, nowMs);
            sdkEvents.emit('blockmeta:status', {
              level: 'warn',
              message: 'blockchain.blockmeta.stream_invalid',
              subject: msg.subject,
              reason: result.reason
            });
          }
        }
      } catch (err_0) {
        if (!cancelled) {
          sdkEvents.emit('blockmeta:status', {
            level: 'warn',
            message: 'blockchain.blockmeta.subscription_failed',
            subject: BLOCKMETA_CONFIRMED_SUBJECT,
            err: err_0 instanceof Error ? err_0.message : String(err_0)
          });
        }
      }
    })();
    void subscription.closed.then(err_1 => {
      if (!cancelled && err_1 instanceof Error) {
        sdkEvents.emit('blockmeta:status', {
          level: 'warn',
          message: 'blockchain.blockmeta.subscription_failed',
          subject: BLOCKMETA_CONFIRMED_SUBJECT,
          err: err_1.message
        });
      }
    });
    return () => {
      cancelled = true;
      store.clear();
      subscription.unsubscribe();
    };
  }, [client, canSubscribe, mode]);
}